import React, { Component, PropTypes } from 'react'
import styled from 'styled-components'
import { translate } from 'react-i18next'
import Promise from 'bluebird'
import R from 'ramda'
import Grid, { Third } from 'grid-styled'
import {
  maxPrice,
  maxArea,
  maxRooms,
  maxYield,
  defaultPriceStep,
  defaultAreaStep,
  defaultRoomsStep,
  defaultYieldStep
} from '../../Utils/Constants'
import { numberWithCommas } from '../../Utils'
import { preferenceDefault } from '../../Utils/Defaults'
import { Fonts, Colors, Metrics } from '../../Themes'
import { searchLocations, searchCategories } from '../../sources/SearchSource'
import {
  addPreference,
  deleteUserPreference,
  fetchPreferences
} from '../../sources/UserSource'
import { updateToken } from '../../actions/AuthActions'
import { validateInput } from '../../Utils/Validations'
import Button from '../../components/Button'
import RangePicker from '../../components/RangePicker'
import SearchableDropdown from '../../components/SearchableDropdown'
import Dropdown from '../../components/Dropdown'
import Input from '../../components/Input'
import Trash from '../../svg-icons/Trash'
import MobileMenu from '../../components/MobileMenu'
import Add from '../../svg-icons/Add'
import SuccessModal from '../../components/Modal/SuccessModal'
import ErrorModal from '../../components/Modal/ErrorModal'
import DialogModal from '../../components/Modal/DialogModal'

const Container = styled.div``

const PreferenceContainer = styled.div`
  border-bottom: ${Metrics.borders.default};
  padding: ${Metrics.margins.base} 0;
  position: relative;
`

const TrashIcon = styled.div`
  position: absolute;
  right: 20px;
  top: ${Metrics.margins.base};

  svg {
    cursor: pointer;
    transition: ease transform 0.2s;

    &:hover {
      transform: scale(1.2);
      transition: ease transform 0.2s;
    }
  }

  @media screen and (max-width: ${Metrics.breakpoints.mobile}) {
    right: 0;
  }
`

const Preference = styled(Grid)`
  padding-left: 0;
  padding-right: 0;
  ${Fonts.style.Label};
  span {
    ${Fonts.style.Text};
  }
`

const CustomGrid = styled(Grid)`
  padding-left: ${props => (props.noPadLeft ? 0 : Metrics.margins.medium)};
  padding-right: ${props => (props.noPadRight ? 0 : Metrics.margins.medium)};
`

const CustomThird = styled(Third)`
  margin-top: ${props => (props.noPadTop ? 0 : Metrics.margins.base)};
  padding-left: ${props => (props.noPadLeft ? 0 : Metrics.margins.medium)};
  padding-right: ${props => (props.noPadRight ? 0 : Metrics.margins.medium)};
`

const PreferenceTitle = styled.div`
  ${Fonts.style.Label};
  color: ${Colors.primary};
`

const FormTitle = styled.div`
  ${Fonts.style.Label};
  display: block;
  margin-top: ${Metrics.margins.base};
  margin-bottom: ${Metrics.margins.small};

  @media screen and (max-width: ${Metrics.breakpoints.mobile}) {
    margin-right: ${Metrics.margins.small};
  }
`
const CustomFormTitle = styled(FormTitle)`
  display: inline-block;
`

const EditArea = styled.section`
  padding: ${Metrics.margins.base} 0;
  border-bottom: ${Metrics.borders.default};

  @media screen and (max-width: ${Metrics.breakpoints.mobile}) {
    display: none;
  }
`

const PreferencesArea = styled.div``

const CustomButton = styled(Button)`
  float: right;
  margin-top: ${Metrics.margins.base};

  @media screen and (max-width: ${Metrics.breakpoints.mobile}) {
    text-align: center;
    background: ${Colors.primary};
    color: ${Colors.white};
    float: inherit;
    width: 100%;
  }
`

class Preferences extends Component {
  constructor (props) {
    super(props)
    this.state = {
      location: [],
      locationSearch: '',
      categories: [],
      modal: null,
      mobileMenuOpen: false,
      userPreferences: [],
      preferenceToDelete: null,
      preference: R.clone(preferenceDefault),
      errors: {
        name: '',
        category: ''
      }
    }
    this.handleSearch = this.handleSearch.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.handleRange = this.handleRange.bind(this)
    this.handleSelect = this.handleSelect.bind(this)
    this.handleUpdate = this.handleUpdate.bind(this)
    this.handleBlur = this.handleBlur.bind(this)
    this.selectPreferenceToDelete = this.selectPreferenceToDelete.bind(this)
    this.toggleModal = this.toggleModal.bind(this)
    this.toggleMobileMenu = this.toggleMobileMenu.bind(this)
    this.cancel = this.cancel.bind(this)
    this.delete = this.delete.bind(this)
    this.validateForm = this.validateForm.bind(this)
  }

  componentWillMount () {
    const { user } = this.props
    let criteria = {}
    if (user.preferences.length > 0) {
      criteria.id = user.preferences.map(el => el.id)
    }

    Promise.all([
      searchCategories(),
      user.preferences.length > 0 ? fetchPreferences(criteria) : []
    ])
      .spread((categoriesResponse, preferencesResponse) => {
        let data = preferencesResponse.data
        this.setState({
          categories: categoriesResponse.data,
          userPreferences: data && data.results.length > 0 ? data.results : []
        })
      })
      .catch(console.error)
  }

  componentWillReceiveProps (nextProps) {
    const { user } = nextProps
    if (user.preferences.length > 0) {
      let criteria = {}
      criteria.id = user.preferences.map(el => el.id)
      fetchPreferences(criteria)
        .then(response => {
          this.setState({ userPreferences: response.data.results })
        })
        .catch(console.error)
    } else {
      this.setState({ userPreferences: [] })
    }
  }

  handleSearch (event) {
    const search = event.target.value

    this.setState({ locationSearch: search }, () => {
      searchLocations({ criteria: search })
        .then(res => {
          const { cities, countries, continents } = res.data

          this.setState({
            location: [
              ...cities.map(city => ({
                ...city,
                type: 'city'
              })),
              ...countries.map(country => ({ ...country, type: 'country' })),
              ...continents.map(continent => ({
                ...continent,
                type: 'continent'
              }))
            ]
          })
        })
        .catch(console.error)
    })
  }

  handleChange (event) {
    const { preference } = this.state
    preference.name = event.target.value
    this.setState({ preference })
  }

  handleRange (minKey, maxKey, value) {
    const { preference } = this.state
    preference[minKey] = value.min
    preference[maxKey] = value.max
    this.setState({ preference })
  }

  handleUpdate () {
    if (this.validateForm()) {
      addPreference(this.props.user.id, this.state.preference)
        .then(response => {
          updateToken(response.data.authToken)
          this.setState({
            locationSearch: '',
            modal: 'success',
            mobileMenuOpen: false,
            preferenceToDelete: null,
            preference: R.clone(preferenceDefault)
          })
        })
        .catch(error => {
          console.error(error)
          this.setState({ modal: 'error' })
        })
    }
  }

  handleSelect (key, el) {
    const { preference, errors } = this.state
    preference[key] = el
    errors[key] = ''

    if (key === 'location') {
      preference[el.type] = el

      return this.setState({
        locationSearch: el.name,
        preference,
        errors
      })
    }

    this.setState({ preference, errors })
  }

  handleBlur (key) {
    const { errors, preference } = this.state
    let newErrors = validateInput(key, preference[key], errors)
    this.setState({ errors: newErrors })
  }

  toggleModal (modalKey) {
    this.setState({ modal: this.state.modal ? null : modalKey })
  }

  toggleMobileMenu () {
    this.setState({ mobileMenuOpen: !this.state.mobileMenuOpen })
  }

  delete () {
    const { user } = this.props
    deleteUserPreference(this.state.preferenceToDelete, user.id)
      .then(response => {
        updateToken(response.data.authToken)
        this.setState({ modal: 'success', preferenceToDelete: null })
      })
      .catch(error => {
        console.log(error)
        this.setState({ modal: 'error', preferenceToDelete: null })
      })
  }

  cancel () {
    this.setState({ modal: null, preferenceToDelete: null })
  }

  selectPreferenceToDelete (id) {
    this.setState({ preferenceToDelete: id, modal: 'delete' })
  }

  validateForm () {
    let { preference, errors } = this.state
    let valid = true
    const keys = Object.keys(errors)
    keys.forEach(key => {
      let returnErros = validateInput(key, preference[key], errors)
      errors = Object.assign({}, errors, returnErros)
      valid = valid && errors[key] === ''
    })
    this.setState({ errors })
    return valid
  }

  render () {
    const { t } = this.props
    const {
      location,
      locationSearch,
      categories,
      preference,
      userPreferences,
      modal,
      errors
    } = this.state
    const list =
      userPreferences.length > 0 &&
      userPreferences.map((pref, index) => {
        return (
          <PreferenceContainer key={`preference-${index}`}>
            <TrashIcon>
              <Trash onClick={() => this.selectPreferenceToDelete(pref.id)} />
            </TrashIcon>
            <CustomGrid>
              <PreferenceTitle>{pref.name}</PreferenceTitle>
            </CustomGrid>
            <CustomGrid>
              <Preference sm={1 / 6}>
                <FormTitle>{t('Type of Asset')}</FormTitle>
                <span key={`key-category-${index}`}>
                  {pref.category && pref.category.name}
                </span>
              </Preference>
              <Preference sm={1 / 6}>
                {pref.city && (
                  <div>
                    <FormTitle>{t('City')}</FormTitle>
                    <span key={`key-city-${index}`}>{`${pref.city.name}`}</span>
                  </div>
                )}
                {pref.country && (
                  <div>
                    <FormTitle>{t('Country')}</FormTitle>
                    <span key={`key-country-${index}`}>
                      {pref.country.name}
                    </span>
                  </div>
                )}
                {pref.continent && (
                  <div>
                    <FormTitle>{t('Continent')}</FormTitle>
                    <span key={`key-continent-${index}`}>
                      {pref.continent.name}
                    </span>
                  </div>
                )}
              </Preference>
              <Preference sm={1 / 6}>
                <FormTitle>{t('Number of Keys')}</FormTitle>
                <span>
                  {pref.minNoRooms} -{' '}
                  {pref.maxNoRooms ? `${pref.maxNoRooms}` : `${maxRooms}+`}
                </span>
              </Preference>
              <Preference sm={1 / 6}>
                <FormTitle>{t('Estimated Yield')}</FormTitle>
                <span>
                  {pref.minYield}% -{' '}
                  {pref.maxYield ? `${pref.maxYield}%` : `${maxYield}%+`}
                </span>
              </Preference>
              <Preference sm={1 / 6}>
                <FormTitle>{t('Price')}</FormTitle>
                <span>
                  €{numberWithCommas(pref.minPrice)} -{' '}
                  {pref.maxPrice ? (
                    `€${numberWithCommas(pref.maxPrice)}`
                  ) : (
                    `€${numberWithCommas(maxPrice)}+`
                  )}
                </span>
              </Preference>
              <Preference sm={1 / 6}>
                <FormTitle>{t('Area')}</FormTitle>
                <span>
                  {numberWithCommas(pref.minArea)}m2 -{' '}
                  {pref.maxArea ? (
                    `${numberWithCommas(pref.maxArea)}m2`
                  ) : (
                    `${numberWithCommas(maxArea)}m2+`
                  )}
                </span>
              </Preference>
            </CustomGrid>
          </PreferenceContainer>
        )
      })

    return (
      <Container>
        <MobileMenu
          label={t('Add Search Request')}
          apply={
            <Button
              onClick={this.handleUpdate}
              label={t('Add')}
              primary
              fullWidth
            />
          }
          toggleMenu={this.toggleMobileMenu}
          open={this.state.mobileMenuOpen}
          icon={<Add />}>
          <Input
            required
            title={<CustomFormTitle>{t('Name')}</CustomFormTitle>}
            handleChange={this.handleChange}
            name='name'
            placeholder={t('Name this search request')}
            value={preference.name}
            handleBlur={() => this.handleBlur('name')}
            error={errors['name']}
          />
          <FormTitle>{t('Property Type')}</FormTitle>
          <Dropdown
            accessor={el => el.name}
            handleSelect={el => this.handleSelect('category', el)}
            placeholder={t('Choose your favourite categories')}
            options={categories}
            selectedOption={preference.category}
            required
            name='category'
            handleBlur={() => this.handleBlur('category')}
            error={errors['category']}
          />
          <FormTitle>{t('Location')}</FormTitle>
          <SearchableDropdown
            required
            value={locationSearch}
            placeholder={t('Choose location')}
            options={location}
            handleSearch={this.handleSearch}
            handleSelect={el => this.handleSelect('location', el)}
            accessor={location => location.name}
            name='location'
            handleBlur={() => this.handleBlur('location')}
            error={errors['location']}
          />
          <RangePicker
            title={<FormTitle>{t('Number of Keys')}</FormTitle>}
            max={maxRooms}
            step={defaultRoomsStep}
            minValue={preference.minNoRooms}
            maxValue={preference.maxNoRooms}
            handleChange={value =>
              this.handleRange('minNoRooms', 'maxNoRooms', value)}
          />
          <RangePicker
            title={<FormTitle>{t('Estimated Yield')}</FormTitle>}
            minValue={preference.minYield}
            maxValue={preference.maxYield}
            unit={'%'}
            rightUnit
            max={maxYield}
            step={defaultYieldStep}
            handleChange={value =>
              this.handleRange('minYield', 'maxYield', value)}
          />
          <RangePicker
            title={<FormTitle>{t('Price')}</FormTitle>}
            minValue={preference.minPrice}
            maxValue={preference.maxPrice}
            unit={'€'}
            handleChange={value =>
              this.handleRange('minPrice', 'maxPrice', value)}
            max={maxPrice}
            step={defaultPriceStep}
          />
          <RangePicker
            title={<FormTitle>{t('Area')}</FormTitle>}
            minValue={preference.minArea}
            maxValue={preference.maxArea}
            handleChange={value =>
              this.handleRange('minArea', 'maxArea', value)}
            max={maxArea}
            unit={'m2'}
            rightUnit
            step={defaultAreaStep}
          />
        </MobileMenu>
        <EditArea>
          <CustomThird noPadLeft>
            <Input
              required
              title={t('Name')}
              handleChange={this.handleChange}
              placeholder={t('Name this preference')}
              value={preference.name}
              handleBlur={() => this.handleBlur('name')}
              error={errors['name']}
            />
          </CustomThird>
          <CustomThird noPadTop>
            <FormTitle>{t('Type of Asset')}</FormTitle>
            <Dropdown
              required
              accessor={el => el.name}
              handleSelect={el => this.handleSelect('category', el)}
              placeholder={t('Choose your favourite categories')}
              options={categories}
              selectedOption={preference.category}
              name='category'
              handleBlur={() => this.handleBlur('category')}
              error={errors['category']}
            />
          </CustomThird>
          <CustomThird noPadTop noPadRight>
            <FormTitle>{t('Location')}</FormTitle>
            <SearchableDropdown
              required
              value={locationSearch}
              placeholder={t('Choose location')}
              options={location}
              handleSearch={this.handleSearch}
              handleSelect={el => this.handleSelect('location', el)}
              accessor={location => location.name}
              name='location'
              handleBlur={() => this.handleBlur('location')}
              error={errors['location']}
            />
          </CustomThird>
          <CustomGrid noPadLeft sm={1 / 4}>
            <RangePicker
              title={<FormTitle>{t('Number of Keys')}</FormTitle>}
              minValue={preference.minNoRooms}
              maxValue={preference.maxNoRooms}
              max={maxRooms}
              step={defaultRoomsStep}
              handleChange={value =>
                this.handleRange('minNoRooms', 'maxNoRooms', value)}
            />
          </CustomGrid>
          <CustomGrid sm={1 / 4}>
            <RangePicker
              title={<FormTitle>{t('Estimated Yield')}</FormTitle>}
              minValue={preference.minYield}
              maxValue={preference.maxYield}
              max={maxYield}
              step={defaultYieldStep}
              unit={'%'}
              rightUnit
              handleChange={value =>
                this.handleRange('minYield', 'maxYield', value)}
            />
          </CustomGrid>
          <CustomGrid sm={1 / 4}>
            <RangePicker
              title={<FormTitle>{t('Price')}</FormTitle>}
              minValue={preference.minPrice}
              maxValue={preference.maxPrice}
              unit={'€'}
              handleChange={value =>
                this.handleRange('minPrice', 'maxPrice', value)}
              max={maxPrice}
              step={defaultPriceStep}
            />
          </CustomGrid>
          <CustomGrid noPadRight sm={1 / 4}>
            <RangePicker
              title={<FormTitle>{t('Area')}</FormTitle>}
              minValue={preference.minArea}
              maxValue={preference.maxArea}
              handleChange={value =>
                this.handleRange('minArea', 'maxArea', value)}
              max={maxArea}
              unit={'m2'}
              rightUnit
              step={defaultAreaStep}
            />
          </CustomGrid>
          <CustomGrid noPadRight noPadLeft>
            <CustomButton label={t('Add')} onClick={this.handleUpdate} />
          </CustomGrid>
        </EditArea>
        <PreferencesArea>
          <SuccessModal
            message={t('Your preferences were successfuly updated')}
            closeAction={() => this.toggleModal('success')}
            header={t('Success')}
            visible={modal === 'success'}
          />
          <ErrorModal
            message={t('There was an error when updating your preferences')}
            closeAction={() => this.toggleModal('error')}
            header={t('Error')}
            visible={modal === 'error'}
          />
          <DialogModal
            message={t('Do you really want to remove this preference?')}
            closeAction={() => this.toggleModal('delete')}
            header={t('Remove Preference')}
            visible={modal === 'delete'}
            buttons={{
              apply: {
                label: t('Delete'),
                onClick: this.delete
              },
              cancel: {
                label: t('Cancel'),
                onClick: this.cancel
              }
            }}
          />
          {list}
        </PreferencesArea>
      </Container>
    )
  }
}

Preferences.propTypes = {
  user: PropTypes.object.isRequired,
  t: PropTypes.func
}

export default translate([ 'common', 'settings', 'forms' ], { wait: true })(
  Preferences
)



// WEBPACK FOOTER //
// ./src/pages/ProfileSettings/Preferences.js